export type Role = 'founder' | 'analyst' | 'senior_analyst';

export type Stage =
  | 'idea'
  | 'analysis'
  | 'awaiting_assignment'
  | 'validation'
  | 'contract'
  | 'bootcamp'
  | 'diligence'
  | 'funding'
  | 'deleted';

export const STAGES: Stage[] = [
  'idea', 
  'analysis', 
  'awaiting_assignment', 
  'validation',
  'contract',
  'bootcamp',
  'diligence', 
  'funding',
];

export const STAGE_LABELS: Record<Stage, string> = {
  idea: 'Idea',
  analysis: 'Analysis',
  awaiting_assignment: 'Awaiting Assignment',
  validation: 'Validation',
  contract: 'Contract',
  bootcamp: 'Bootcamp',
  diligence: 'Due Diligence',
  funding: 'Funding',
  deleted: 'Deleted',
};

export const ROLE_LABELS: Record<Role, string> = {
  founder: 'Founder',
  analyst: 'Analyst',
  senior_analyst: 'Senior Analyst',
};

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  password: string;
}

// New interface for traction data
export interface TractionData {
  customerInterviews: number;
  waitlistSignups: number;
  lettersOfIntent: number;
  pilotCustomers: number;
  mvpStatus: 'none' | 'in_development' | 'beta' | 'live';
  mvpUrl?: string;
  monthlyActiveUsers?: number;
  revenueRunRate?: number;
}

// New interface for competitive analysis
export interface CompetitiveData {
  directCompetitors: string[];
  indirectCompetitors: string[];
  competitiveAdvantage: string;
  barriersToEntry: string[];
  substituteProducts: string[];
}

// New interface for financial projections
export interface FinancialData {
  fundingAsk: number;
  useOfFunds: {
    product: number;
    marketing: number;
    team: number;
    operations: number;
    legal: number;
    other: number;
  };
  runwayMonths: number;
  burnRate: number;
  revenueProjection: {
    month1: number;
    month3: number;
    month6: number;
    month12: number;
  };
  breakevenMonth: number;
}

// New interface for go-to-market
export interface GTMData {
  customerChannels: string[];
  salesStrategy: string;
  marketingPlan: string;
  partnerships: string[];
  targetEarlyAdopters: string;
}

// New interface for IP and technology
export interface IPData {
  patentStatus: 'none' | 'filed' | 'provisional' | 'granted';
  patentNumbers?: string[];
  proprietaryTechnology: string;
  technicalArchitecture: string;
  developmentStage: 'idea' | 'prototype' | 'alpha' | 'beta' | 'live';
  techStack: string[];
}

// New interface for risks
export interface RiskData {
  regulatoryHurdles: string[];
  complianceRequirements: string[];
  keyRisks: Array<{
    risk: string;
    impact: 'low' | 'medium' | 'high';
    mitigation: string;
  }>;
  marketRisks: string[];
  executionRisks: string[];
}

export interface AnalysisScores {
  problemClarity: number;
  marketViability: number;
  feasibility: number;
  revenueLogic: number;
  executionReadiness: number;
  tractionScore: number;
  competitiveMoats: number;
  teamStrength: number;
  overall: number;
  strengths: string[];
  weaknesses: string[];
  redFlags: string[];
  recommendations: string[];
  verdict: 'reject' | 'maybe' | 'meet' | 'invest';
}

export interface Meeting {
  id: string;
  pitchId: string;
  agenda: string[];
  outcome: 'pending' | 'approved' | 'rejected';
  date: string;
  feedback?: string;
}

export interface DiligenceData {
  businessRegistration: string;
  physicalAddress: string;
  pinLocation: string;
  workforceSize: string;
  businessType: string;
  verified: boolean;
  legalStructure?: string;
  incorporationDate?: string;
  taxCompliance?: boolean;
}

export interface Milestone {
  id: string;
  title: string;
  completed: boolean;
  trancheAmount: number;
  approved: boolean;
  dueDate?: string;
  description?: string;
}

export interface BootcampDay {
  day: number;
  week: number;
  title: string;
  completed: boolean;
  deliverables?: string[];
}

export interface AppNotification {
  id: string;
  userId: string;
  message: string;
  pitchId: string;
  tab?: string;
  read: boolean;
  createdAt: string;
  type?: 'info' | 'success' | 'warning' | 'error';
}

export interface Pitch {
  id: string;
  founderId: string;
  assignedAnalystId: string | null;
  assignedBySeniorId: string | null;
  
  // Core information
  companyName: string;
  tagline: string;
  problem: string;
  solution: string;
  marketSize: string;
  businessModel: string;
  team: string;
  
  // New enhanced sections
  traction: TractionData;
  competitive: CompetitiveData;
  financial: FinancialData;
  gtm: GTMData;
  ip: IPData;
  risks: RiskData;
  
  // Existing fields
  stage: Stage;
  analysisScores: AnalysisScores | null;
  meetings: Meeting[];
  diligenceData: DiligenceData | null;
  contractSignedFounder: boolean;
  contractSignedAnalyst: boolean;
  bootcampProgress: BootcampDay[];
  milestones: Milestone[];
  escrowBalance: number;
  deletedAt?: string;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
}